// Admin Team Cup tab — only rendered when info.format === 'Team Cup'.
//
// Sections:
//   1. CONFIGURATION — scoring system radio, Scats / CTH toggles + $,
//      Use Handicaps toggle.
//   2. TEAMS — add unlimited teams, assign players to teams (chip toggle).
//   3. GROUPS / MATCHUPS — add groups (foursomes) with their own auto-
//      generated code. Each group's playerIds drive who's scored together.
//   4. LIVE / SETTLEMENT — once scores start flowing in, show team totals,
//      Scats running leaderboard, CTH board. When status === 'final' the
//      dollar payouts settle.

import React, { useEffect, useMemo, useState } from 'react'
import {
  watchTeamCup, updateTeamCupConfig, DEFAULT_TEAM_CUP_CONFIG,
  createTeamCupTeam, updateTeamCupTeam, deleteTeamCupTeam,
  createTeamCupGroup, updateTeamCupGroup, deleteTeamCupGroup,
  setTeamCupStatus,
} from '../lib/teamCupData.js'
import {
  computeTeamMatchResults, computeScatPayouts, computeCthValues,
  strokesForRound, DEFAULT_STROKE_INDEX,
} from '../lib/teamCupScoring.js'
import { calcCourseHandicap, calcPlayingHandicap } from '../lib/handicap.js'

const ALLOWANCES = [100, 90, 85, 80]

export default function TeamCupTab({ tournamentId, tournament, players, pars }) {
  const info = tournament?.info || {}
  const config = { ...DEFAULT_TEAM_CUP_CONFIG, ...(info.teamCupConfig || {}) }
  const [data, setData] = useState({})

  useEffect(() => {
    const unsub = watchTeamCup(tournamentId, setData)
    return () => unsub()
  }, [tournamentId])

  const teams = entriesAsList(data?.teams)
  const groups = entriesAsList(data?.groups)
  const scoresByGroup = data?.scores || {}
  const cthSubs = data?.cth || {}
  const isFinal = data?.status === 'final'

  // ── Derived: stroke allocation per player (when handicaps on) ─────────
  const handicapsOn = !!config.handicapsOn && (info.handicapMode && info.handicapMode !== 'none')
  const slopeRating = Number(info?.slopeRating) || 113
  const courseRating = Number(info?.courseRating) || (Number(info?.coursePar) || 72)
  const coursePar = Number(info?.coursePar) || 72
  const allowance = Number(info?.handicapAllowance) || 100
  const courseHandicaps = useMemo(() => {
    const out = {}
    if (!handicapsOn) return out
    for (const p of players) {
      const hi = Number(p.handicapIndex)
      if (!Number.isFinite(hi)) continue
      const ch = calcCourseHandicap(hi, slopeRating, courseRating, coursePar)
      const ph = calcPlayingHandicap(ch, allowance)
      out[p.id] = ph
    }
    return out
  }, [handicapsOn, players, slopeRating, courseRating, coursePar, allowance])
  const strokesByPlayer = useMemo(
    () => strokesForRound(courseHandicaps, DEFAULT_STROKE_INDEX),
    [courseHandicaps]
  )

  // Pre-compute per-group team match results.
  const groupResults = useMemo(() => groups.map(g => {
    // For each group: the "teams" passed to the scoring engine are the
    // subset of the configured teams who have at least one player in
    // this group.
    const groupPids = new Set(g.playerIds || [])
    const groupTeams = teams
      .map(t => ({
        id: t.id,
        name: t.name || 'Team',
        playerIds: (t.playerIds || []).filter(pid => groupPids.has(pid)),
      }))
      .filter(t => t.playerIds.length > 0)
    const res = computeTeamMatchResults({
      teams: groupTeams,
      scoresByHole: scoresByGroup?.[g.id] || {},
      strokesByPlayer,
      handicapsOn,
      scoringSystem: config.scoringSystem,
    })
    return { group: g, teams: groupTeams, ...res }
  }), [groups, teams, scoresByGroup, strokesByPlayer, handicapsOn, config.scoringSystem])

  // Aggregate team totals across all groups.
  const teamTotals = useMemo(() => {
    const out = {}
    teams.forEach(t => { out[t.id] = 0 })
    groupResults.forEach(gr => {
      Object.entries(gr.teamPoints).forEach(([tid, pts]) => {
        if (out[tid] != null) out[tid] += pts
      })
    })
    return out
  }, [groupResults, teams])

  const allPlayerIds = useMemo(
    () => Array.from(new Set(groups.flatMap(g => g.playerIds || []))),
    [groups]
  )
  const scats = useMemo(() => computeScatPayouts({
    playerIds: allPlayerIds,
    scoresByGroup,
    perHole: Number(config.scatsPerHole) || 0,
    roundFinal: isFinal,
  }), [allPlayerIds, scoresByGroup, config.scatsPerHole, isFinal])

  const cth = useMemo(() => computeCthValues({
    pars: pars || [],
    cthByHole: cthSubs,
    groupCodes: groups.map(g => g.id),
    perPar3: Number(config.cthPerPar3) || 0,
  }), [pars, cthSubs, groups, config.cthPerPar3])

  return (
    <div className="space-y-4">
      <ScoringSystemCard tournamentId={tournamentId} config={config} />
      <ConfigCard tournamentId={tournamentId} config={config} />
      <TeamsCard tournamentId={tournamentId} teams={teams} players={players} />
      <GroupsCard tournamentId={tournamentId} groups={groups} players={players} />
      <LiveCard
        teams={teams}
        teamTotals={teamTotals}
        groupResults={groupResults}
        scats={config.scatsEnabled ? scats : null}
        cth={config.cthEnabled ? cth : null}
        players={players}
        pars={pars}
        isFinal={isFinal}
        config={config}
        onToggleFinal={(v) => setTeamCupStatus(tournamentId, v ? 'final' : 'open')}
      />
    </div>
  )
}

// ── Scoring System Card — dedicated, full-width, top-of-tab.
//
// Spec called this out specifically: the admin must NOT miss which of the
// two scoring systems they're using. Renders as the first card in the
// Team Cup tab with two big tile-style radios; selected option gets a
// thick green border, green fill, and a check badge in the top-right
// corner. Bold label + larger hint copy than the rest of the form.
function ScoringSystemCard({ tournamentId, config }) {
  const save = (patch) => updateTeamCupConfig(tournamentId, patch)
  const options = [
    {
      id: 'official',
      title: 'Official Team Cup Scoring',
      hint: '1 point per hole won, ½ point each for a halved hole. Max 18 points per match.',
    },
    {
      id: 'custom',
      title: 'Custom Scoring',
      hint: '1 point per hole + 1 bonus point for winning the front nine + 1 bonus point for winning the back nine. Max 20 points per match.',
    },
  ]
  return (
    <div className="pbcc-card-light p-5">
      <span className="pbcc-gold-rule" aria-hidden="true" />
      <h2 className="pbcc-h2 text-xl mt-1">Scoring System</h2>
      <p className="text-sm text-gray-700 mt-1 leading-snug">
        Pick how points are awarded this tournament. <b>You can change this anytime</b> until the round goes final.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        {options.map((opt) => {
          const active = config.scoringSystem === opt.id
          return (
            <label
              key={opt.id}
              className={`relative cursor-pointer rounded-xl p-4 transition shadow-sm ${
                active
                  ? 'bg-green-50 ring-2 ring-green-700 border border-green-700'
                  : 'bg-white border-2 border-gray-200 hover:border-green-700 hover:bg-green-50/40'
              }`}>
              <input
                type="radio"
                name="scoringSystem"
                value={opt.id}
                checked={active}
                onChange={() => save({ scoringSystem: opt.id })}
                className="sr-only"
                aria-label={opt.title}
              />
              {/* Selected-state check badge in the top-right corner */}
              {active ? (
                <span
                  className="absolute top-2.5 right-2.5 flex items-center justify-center rounded-full bg-green-700 text-white"
                  style={{ width: 24, height: 24 }}
                  aria-hidden="true">
                  <svg viewBox="0 0 20 20" width="14" height="14" fill="none">
                    <path d="M5 10.5l3.2 3.2L15 6.8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              ) : null}
              <div className="flex items-start gap-3">
                {/* Visible radio dot (kept alongside sr-only input for click-target + a11y) */}
                <div
                  className={`mt-0.5 flex items-center justify-center rounded-full border-2 ${
                    active ? 'border-green-700' : 'border-gray-400'
                  }`}
                  style={{ width: 22, height: 22 }}
                  aria-hidden="true">
                  {active ? (
                    <div className="rounded-full bg-green-700" style={{ width: 12, height: 12 }} />
                  ) : null}
                </div>
                <div className="flex-1 pr-6">
                  <div className={`font-extrabold leading-tight ${active ? 'text-green-900' : 'text-gray-900'}`} style={{ fontSize: 16 }}>
                    {opt.title}
                  </div>
                  <div className="text-sm text-gray-700 mt-1 leading-snug">
                    {opt.hint}
                  </div>
                </div>
              </div>
            </label>
          )
        })}
      </div>
    </div>
  )
}

// ── Configuration card ─────────────────────────────────────────────────
function ConfigCard({ tournamentId, config }) {
  const save = (patch) => updateTeamCupConfig(tournamentId, patch)
  return (
    <div className="pbcc-card-light p-5">
      <span className="pbcc-gold-rule" aria-hidden="true" />
      <h2 className="pbcc-h2 text-xl mt-1">Side Bets & Handicaps</h2>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <ToggleAmount
          label="Scats" hint="Low gross outright across ALL players per hole. Ties cancel. $ shown when final."
          enabled={config.scatsEnabled} amount={config.scatsPerHole}
          onChangeEnabled={(v) => save({ scatsEnabled: v })}
          onChangeAmount={(v) => save({ scatsPerHole: v })}
          amountLabel="$ / hole" />
        <ToggleAmount
          label="Closest to Hole" hint="Per par-3 pot. Unclaimed pots split forward to remaining par 3s."
          enabled={config.cthEnabled} amount={config.cthPerPar3}
          onChangeEnabled={(v) => save({ cthEnabled: v })}
          onChangeAmount={(v) => save({ cthPerPar3: v })}
          amountLabel="$ / par 3" />
      </div>

      <div className="mt-4 flex items-center gap-3">
        <input
          type="checkbox"
          checked={!!config.handicapsOn}
          onChange={(e) => save({ handicapsOn: e.target.checked })} />
        <div>
          <div className="font-bold text-sm">Use Handicaps (net scoring)</div>
          <div className="text-xs text-gray-600">
            When ON, hole winners are decided by net best ball. Scats and CTH still use gross.
          </div>
        </div>
      </div>
    </div>
  )
}

function ToggleAmount({ label, hint, enabled, amount, onChangeEnabled, onChangeAmount, amountLabel }) {
  return (
    <div className={`p-3 rounded-lg border ${enabled ? 'border-green-700 bg-green-50' : 'border-gray-200 bg-white'}`}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={(e) => onChangeEnabled(e.target.checked)} />
        <div className="font-bold text-sm">{label}</div>
      </label>
      <div className="text-xs text-gray-600 mt-1">{hint}</div>
      {enabled ? (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-gray-600">{amountLabel}</span>
          <input
            type="number" min="0" step="0.5"
            className="input w-28"
            value={amount ?? ''}
            onChange={(e) => onChangeAmount(Number(e.target.value) || 0)} />
        </div>
      ) : null}
    </div>
  )
}

// ── Teams card ────────────────────────────────────────────────────────
function TeamsCard({ tournamentId, teams, players }) {
  return (
    <div className="pbcc-card-light p-5">
      <div className="flex justify-between items-center">
        <h2 className="pbcc-h2 text-xl">Teams</h2>
        <button className="pbcc-btn-green" onClick={() => createTeamCupTeam(tournamentId, { name: '' })}>
          + Add Team
        </button>
      </div>
      <p className="text-xs text-gray-600 mt-1">Minimum 2 teams. Every player should be on exactly one team.</p>
      <div className="mt-3 space-y-3">
        {teams.length === 0 ? (
          <div className="text-sm text-gray-500 italic">No teams yet — add at least two.</div>
        ) : null}
        {teams.map(t => (
          <TeamRow key={t.id} team={t} teams={teams} tournamentId={tournamentId} players={players} />
        ))}
      </div>
    </div>
  )
}

function TeamRow({ team, teams, tournamentId, players }) {
  const togglePlayer = async (pid) => {
    const has = (team.playerIds || []).includes(pid)
    // Remove from every team first, then add to this one.
    await Promise.all(teams
      .filter(t => t.id !== team.id)
      .map(t => updateTeamCupTeam(tournamentId, t.id, {
        playerIds: (t.playerIds || []).filter(x => x !== pid),
      })))
    const next = has
      ? (team.playerIds || []).filter(x => x !== pid)
      : [...(team.playerIds || []), pid]
    await updateTeamCupTeam(tournamentId, team.id, { playerIds: next })
  }
  return (
    <div className="p-3 rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center gap-2">
        <input
          className="input flex-1"
          value={team.name || ''}
          placeholder="Team name"
          onChange={(e) => updateTeamCupTeam(tournamentId, team.id, { name: e.target.value })} />
        <button
          className="btn-danger"
          onClick={() => {
            if (confirm(`Delete team ${team.name || 'unnamed'}?`)) deleteTeamCupTeam(tournamentId, team.id)
          }}>✕</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {players.length === 0 ? <span className="text-xs text-gray-500 italic">No players registered yet.</span> : null}
        {players.map(p => {
          const on = (team.playerIds || []).includes(p.id)
          return (
            <button
              key={p.id}
              onClick={() => togglePlayer(p.id)}
              className={`text-xs px-2 py-1 rounded-full border ${on ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-700 border-gray-300'}`}>
              {on ? '✓ ' : ''}{p.name || p.id}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Groups card ───────────────────────────────────────────────────────
function GroupsCard({ tournamentId, groups, players }) {
  return (
    <div className="pbcc-card-light p-5">
      <div className="flex justify-between items-center">
        <h2 className="pbcc-h2 text-xl">Groups (Matchups)</h2>
        <button className="pbcc-btn-green" onClick={() => createTeamCupGroup(tournamentId, {})}>
          + Add Group
        </button>
      </div>
      <p className="text-xs text-gray-600 mt-1">
        Each group is a foursome with its own 4-letter access code. The scorekeeper opens
        <code className="px-1">/score?t=…&team=&lt;groupCode&gt;</code> to enter scores.
      </p>
      <div className="mt-3 space-y-3">
        {groups.length === 0 ? (
          <div className="text-sm text-gray-500 italic">No groups yet.</div>
        ) : null}
        {groups.map(g => (
          <GroupRow key={g.id} group={g} tournamentId={tournamentId} players={players} />
        ))}
      </div>
    </div>
  )
}

function GroupRow({ group, tournamentId, players }) {
  const togglePlayer = async (pid) => {
    const has = (group.playerIds || []).includes(pid)
    const next = has
      ? (group.playerIds || []).filter(x => x !== pid)
      : [...(group.playerIds || []), pid]
    await updateTeamCupGroup(tournamentId, group.id, { playerIds: next })
  }
  return (
    <div className="p-3 rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center gap-2">
        <span className="font-mono font-bold text-lg text-green-800">{group.code}</span>
        <input
          className="input flex-1"
          value={group.name || ''}
          placeholder="Group name (optional)"
          onChange={(e) => updateTeamCupGroup(tournamentId, group.id, { name: e.target.value })} />
        <button
          className="btn-danger"
          onClick={() => {
            if (confirm(`Delete group ${group.code}? Scores will be removed.`)) deleteTeamCupGroup(tournamentId, group.id)
          }}>✕</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {players.map(p => {
          const on = (group.playerIds || []).includes(p.id)
          return (
            <button
              key={p.id}
              onClick={() => togglePlayer(p.id)}
              className={`text-xs px-2 py-1 rounded-full border ${on ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-700 border-gray-300'}`}>
              {on ? '✓ ' : ''}{p.name || p.id}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Live / settlement card ────────────────────────────────────────────
function LiveCard({ teams, teamTotals, groupResults, scats, cth, players, pars, isFinal, config, onToggleFinal }) {
  const playerName = (pid) => (players.find(p => p.id === pid)?.name) || pid
  const ranked = [...teams].sort((a, b) => (teamTotals[b.id] || 0) - (teamTotals[a.id] || 0))
  return (
    <div className="pbcc-card-light p-5">
      <div className="flex justify-between items-center">
        <h2 className="pbcc-h2 text-xl">Live Standings & Settlement</h2>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={isFinal} onChange={(e) => onToggleFinal(e.target.checked)} />
          Mark round as FINAL
        </label>
      </div>

      {/* Team standings */}
      <div className="mt-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-600">Team Standings</h3>
        {teams.length === 0 ? (
          <div className="text-sm text-gray-500 italic mt-2">Configure at least two teams.</div>
        ) : (
          <div className="mt-2 space-y-1">
            {ranked.map((t, i) => (
              <div key={t.id} className="flex items-center justify-between p-2 rounded bg-white border border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500 w-5">{i + 1}.</span>
                  <span className="font-bold">{t.name || 'Team'}</span>
                </div>
                <span className="text-lg font-extrabold text-green-800">{fmtPts(teamTotals[t.id] || 0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-group results */}
      <div className="mt-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-600">Group Results</h3>
        <div className="mt-2 space-y-2">
          {groupResults.length === 0 ? (
            <div className="text-sm text-gray-500 italic">No groups configured.</div>
          ) : null}
          {groupResults.map(gr => (
            <div key={gr.group.id} className="p-2 rounded bg-white border border-gray-100">
              <div className="flex justify-between items-baseline">
                <span className="font-mono font-bold text-green-800">{gr.group.code}</span>
                <span className="text-xs text-gray-500">{gr.holes.filter(h => h.winnerId != null).length}/18 holes decided</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-3 text-xs">
                {gr.teams.map(t => (
                  <span key={t.id} className="font-bold">
                    {t.name}: {fmtPts(gr.teamPoints[t.id] || 0)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scats */}
      {scats ? (
        <div className="mt-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-600">Scats</h3>
          <p className="text-xs text-gray-500">Low gross across all players. {isFinal ? 'Dollars are final.' : 'Dollars hidden until round is final.'}</p>
          <div className="mt-2 max-h-48 overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500"><tr><th className="text-left py-1">Player</th><th className="text-right py-1">Holes</th><th className="text-right py-1">{isFinal ? '$' : ''}</th></tr></thead>
              <tbody>
                {Object.entries(scats.holesWonByPlayer)
                  .sort((a, b) => b[1] - a[1])
                  .map(([pid, n]) => (
                    <tr key={pid} className="border-t border-gray-100">
                      <td className="py-1">{playerName(pid)}</td>
                      <td className="text-right py-1 font-bold">{n}</td>
                      <td className={`text-right py-1 font-bold ${(scats.payoutsByPlayer[pid] || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {isFinal ? formatMoney(scats.payoutsByPlayer[pid] || 0) : ''}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* CTH */}
      {cth ? (
        <div className="mt-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-600">Closest to Hole</h3>
          <p className="text-xs text-gray-500">Par 3 contests, carry forward when none claimed.</p>
          <div className="mt-2">
            {cth.perHole.length === 0 ? (
              <div className="text-xs text-gray-500 italic">No par 3s on this course.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-gray-500"><tr><th className="text-left py-1">Hole</th><th className="text-left py-1">Winner</th><th className="text-right py-1">$ Paid</th></tr></thead>
                <tbody>
                  {cth.perHole.map(r => (
                    <tr key={r.hole} className="border-t border-gray-100">
                      <td className="py-1">{r.hole}</td>
                      <td className="py-1">{r.winnerId === 'none' ? 'None (carry)' : r.winnerId ? playerName(r.winnerId) : '—'}</td>
                      <td className="text-right py-1 font-bold">{r.paid > 0 ? formatMoney(r.paid) : (r.carryFromHere > 0 ? `→ ${formatMoney(r.carryFromHere)}` : '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {cth.leftoverBucket > 0 ? (
              <div className="text-xs text-amber-700 font-bold mt-2">Unclaimed CTH: {formatMoney(cth.leftoverBucket)}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function entriesAsList(obj) {
  return Object.entries(obj || {}).map(([id, v]) => ({ id, ...v }))
}
function fmtPts(n) {
  if (Number.isInteger(n)) return String(n)
  return String(Math.round(n * 10) / 10)
}
function formatMoney(n) {
  const v = Number(n) || 0
  const sign = v >= 0 ? '+' : '-'
  return `${sign}$${Math.abs(v).toFixed(2)}`
}
